/**
 * Touch input provider with virtual joysticks (nipplejs)
 * Converts analog joystick input to key-equivalent states
 */

import nipplejs, {
  type JoystickManager,
  type EventData,
  type JoystickOutputData,
} from "nipplejs";
import type { Vec2 } from "@game/core";
import type { InputProvider, RawInputState } from "@game/domain";

export class TouchInput implements InputProvider {
  private canvas: HTMLCanvasElement;

  // Joysticks
  private leftJoystick: JoystickManager | null = null;
  private rightJoystick: JoystickManager | null = null;

  // State (analog values from joysticks)
  private movement: Vec2 = { x: 0, y: 0 };
  private aimDirection: Vec2 = { x: 0, y: -1 };

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.setupJoysticks();
  }

  private setupJoysticks(): void {
    // Get dedicated zones for each joystick
    const leftZone = document.getElementById("joystick-left");
    const rightZone = document.getElementById("joystick-right");

    if (!leftZone || !rightZone) {
      console.error(
        "[TouchInput] Joystick zones not found in DOM. Touch controls unavailable.",
      );
      return;
    }

    // Left joystick (movement)
    this.leftJoystick = nipplejs.create({
      zone: leftZone,
      mode: "static",
      position: { left: "50%", bottom: "50%" },
      color: "rgba(0, 255, 0, 0.3)",
      size: 120,
    });

    this.leftJoystick.on(
      "move",
      (_evt: EventData, data: JoystickOutputData) => {
        if (data.vector) {
          this.movement = {
            x: data.vector.x,
            y: data.vector.y,
          };
        }
      },
    );

    this.leftJoystick.on("end", () => {
      this.movement = { x: 0, y: 0 };
    });

    // Right joystick (aim)
    this.rightJoystick = nipplejs.create({
      zone: rightZone,
      mode: "static",
      position: { left: "50%", bottom: "50%" },
      color: "rgba(255, 0, 0, 0.3)",
      size: 120,
    });

    this.rightJoystick.on(
      "move",
      (_evt: EventData, data: JoystickOutputData) => {
        if (data.vector) {
          this.aimDirection = {
            x: data.vector.x,
            y: -data.vector.y,
          };
        }
      },
    );

    this.rightJoystick.on("end", () => {
      // Keep last aim direction (don't reset)
    });
  }

  update(_deltaTime: number): void {
    // Touch provider is event-driven, no frame updates needed
  }

  getInputState(): RawInputState {
    // Convert analog joystick to key-equivalent states (threshold 0.3)
    const threshold = 0.3;

    // Convert aim direction to simulated screen coordinates
    // Server will calculate offset from these raw coordinates
    const screenCenterX = this.canvas.width / 2;
    const screenCenterY = this.canvas.height / 2;
    const aimOffsetX = this.aimDirection.x * 100; // Scale joystick to pixels
    const aimOffsetY = this.aimDirection.y * 100;

    return {
      keyForward: this.movement.y > threshold,
      keyBack: this.movement.y < -threshold,
      keyLeft: this.movement.x < -threshold,
      keyRight: this.movement.x > threshold,
      keyBoost: false, // Could add boost button later
      keyShift: false, // No shift on mobile
      mouseScreenX: screenCenterX + aimOffsetX,
      mouseScreenY: screenCenterY + aimOffsetY,
      canvasWidth: this.canvas.width,
      canvasHeight: this.canvas.height,
      mouseShoot: true, // Auto-shoot on mobile
      timestamp: performance.now(),
    };
  }

  destroy(): void {
    this.leftJoystick?.destroy();
    this.rightJoystick?.destroy();
  }
}
